import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { imagingTestAPI, patientAPI, doctorAPI, csrfHeaders } from '../../lib/api';
import { formatDateTime, getStatusColor, formatCurrency, apiErrorMessage } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { Scan, Plus, ClipboardCheck } from 'lucide-react';
import { toast } from 'sonner';
import SearchableSelect from '../../components/SearchableSelect/SearchableSelect';
import SuggestItemModal from '../../components/SuggestItem/SuggestItemModal';
import { logger } from '../../lib/logger';

const IMAGING_TYPES = ['X-Ray', 'CT Scan', 'MRI', 'Ultrasound', 'Mammography', 'Fluoroscopy', 'DEXA Scan', 'PET Scan'];

const BODY_PARTS = ['Head', 'Neck', 'Chest', 'Abdomen', 'Pelvis', 'Spine', 'Upper Limb', 'Lower Limb', 'Whole Body', 'Other'];

const ImagingTestList = () => {
  const { user, hasRole } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [tests, setTests] = useState([]);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [resultDialogOpen, setResultDialogOpen] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientHistory, setPatientHistory] = useState([]);
  const [resultData, setResultData] = useState({ report: '', status: 'completed' });
  const [showSuggestModal, setShowSuggestModal] = useState(false);
  const [currentTestIndex, setCurrentTestIndex] = useState(null);
  
  // Get patient_id from URL
  const queryParams = new URLSearchParams(location.search);
  const patientIdFromUrl = queryParams.get('patient_id');
  const [formData, setFormData] = useState({
    patient_id: patientIdFromUrl || '',
    test_type: '',
    test_name: '',
    body_part: '',
    requested_by: '',
    urgency: 'normal',
    clinical_notes: '',
    price: 0,
  });

  useEffect(() => {
    if (patientIdFromUrl) {
      fetchPatientHistory(patientIdFromUrl);
      setDialogOpen(true);
    }
  }, [patientIdFromUrl]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = patientIdFromUrl ? { patient_id: patientIdFromUrl } : {};
      if (statusFilter !== 'all') params.status = statusFilter;
      
      const [testRes, patRes, docRes] = await Promise.all([
        imagingTestAPI.getAll(params),
        patientAPI.getAll({}),
        doctorAPI.getAll(),
      ]);
      
      const testsData = testRes.data.imaging_tests || testRes.data || [];
      const patientsData = patRes.data.patients || patRes.data || [];
      const doctorsData = docRes.data.doctors || docRes.data || [];
      
      setTests(testsData);
      setPatients(patientsData);
      setDoctors(doctorsData);
      
      // Set selected patient if from URL
      if (patientIdFromUrl) {
        const patient = patientsData.find(p => p.id === patientIdFromUrl);
        setSelectedPatient(patient);
      }
    } catch (error) {
      logger.error('Error loading imaging test data:', error);
      toast.error('Failed to load data. Please refresh the page.');
      // Set empty arrays on error, not null
      setTests([]);
      setPatients([]);
      setDoctors([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, patientIdFromUrl]);


  useEffect(() => {
    fetchData();
  }, [statusFilter, fetchData]);
  const fetchPatientHistory = async (patientId) => {
    try {
      const response = await imagingTestAPI.getAll({ patient_id: patientId });
      setPatientHistory(response.data.imaging_tests || response.data || []);
    } catch (error) {
      logger.error('Failed to load patient history');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        test_name: `${formData.test_type} - ${formData.body_part}`,
        requested_by: user.id,  // Auto-fill with current user
      };
      
      // Auto-create suggestion for new imaging test if needed (non-blocking)
      if (formData.test_type && formData.test_type.trim()) {
        try {
          const searchResponse = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/imaging-tests/search?q=${encodeURIComponent(formData.test_type)}`,
            { credentials: 'include' }
          );
          const searchData = await searchResponse.json();
          const exists = searchData.results?.some(
            item => item.name.toLowerCase() === formData.test_type.toLowerCase()
          );
          
          if (!exists) {
            await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/suggest-master-item`, {
              method: 'POST',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
              body: JSON.stringify({
                item_type: 'imaging_test',
                item_name: formData.test_type.trim(),
                item_details: {
                  description: `Auto-suggested from imaging order`
                }
              })
            });
          }
        } catch (err) {
          logger.debug('Auto-suggestion failed for:', formData.test_type);
        }
      }
      
      await imagingTestAPI.create(data);
      toast.success('Imaging test ordered successfully');
      setDialogOpen(false);
      
      // If came from patient detail, navigate back to patient detail page
      if (patientIdFromUrl) {
        navigate(`/patients/${patientIdFromUrl}`);
      } else {
        setFormData({
          patient_id: '',
          test_type: '',
          test_name: '',
          body_part: '',
          requested_by: '',
          urgency: 'normal',
          clinical_notes: '',
          price: 0,
        });
        fetchData();
      }
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to create imaging test'));
    }
  };

  const openResultDialog = (test) => {
    setSelectedTest(test);
    setResultData({ report: test.report || '', status: 'completed' });
    setResultDialogOpen(true);
  };

  const handleUpdateResult = async () => {
    try {
      await imagingTestAPI.update(selectedTest.id, resultData);
      toast.success('Report updated');
      setResultDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update report');
    }
  };

  return (
    <div data-testid="imaging-tests-page">
      {loading ? (
        <div className="min-h-screen flex items-center justify-center bg-slate-50">
          <div className="text-slate-500">Loading imaging tests...</div>
        </div>
      ) : (
        <>
      <header className="page-header glass-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-[Manrope]">Imaging Tests</h1>
            <p className="text-slate-500">Manage radiology and imaging orders</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open && patientIdFromUrl) {
              navigate(`/patients/${patientIdFromUrl}`);
            }
          }}>
            <DialogTrigger asChild>
              <Button className="bg-red-500 hover:bg-red-600 rounded-full" data-testid="new-imaging-test-btn">
                <Plus className="w-4 h-4 mr-2" />
                Order Imaging
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-[Manrope]">
                  {selectedPatient ? `Order Imaging for ${selectedPatient.full_name}` : 'Order Imaging Test'}
                </DialogTitle>
              </DialogHeader>

              {/* Patient History */}
              {selectedPatient && patientHistory.length > 0 && (
                <Card className="mt-4">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-semibold">Previous Imaging Tests</CardTitle>
                  </CardHeader>
                  <CardContent className="max-h-40 overflow-y-auto">
                    <div className="space-y-2">
                      {patientHistory.slice(0, 5).map((test) => (
                        <div key={test.id} className="p-2 bg-slate-50 rounded-md text-xs">
                          <div className="flex justify-between">
                            <span className="font-medium">{test.test_type} - {test.body_part}</span>
                            <Badge className={test.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                              {test.status}
                            </Badge>
                          </div>
                          <p className="text-slate-500">{formatDateTime(test.created_at)}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                {!patientIdFromUrl && (
                  <div>
                    <Label>Patient *</Label>
                    <Select value={formData.patient_id} onValueChange={(v) => { setFormData({...formData, patient_id: v}); fetchPatientHistory(v); }}>
                      <SelectTrigger data-testid="select-patient">
                        <SelectValue placeholder="Select patient" />
                      </SelectTrigger>
                      <SelectContent position="popper" sideOffset={5}>
                        {patients.map(p => (
                          <SelectItem key={p.id} value={p.id}>{p.full_name} ({p.patient_id})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {patientIdFromUrl && selectedPatient && (
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-800">
                      <strong>Patient:</strong> {selectedPatient.full_name} ({selectedPatient.patient_id})
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Imaging Type *</Label>
                    <SearchableSelect
                      value={formData.test_type}
                      onValueChange={(v) => setFormData({...formData, test_type: v})}
                      onSuggestNew={() => setShowSuggestModal(true)}
                      searchEndpoint={`${process.env.REACT_APP_BACKEND_URL}/api/imaging-tests/search`}
                      placeholder="Search imaging type..."
                      searchPlaceholder="Type to search imaging tests..."
                      emptyMessage="No imaging tests found"
                    />
                  </div>
                  <div>
                    <Label>Body Part *</Label>
                    <Select value={formData.body_part} onValueChange={(v) => setFormData({...formData, body_part: v})}>
                      <SelectTrigger data-testid="select-body-part">
                        <SelectValue placeholder="Select part" />
                      </SelectTrigger>
                      <SelectContent>
                        {BODY_PARTS.map(part => (
                          <SelectItem key={part} value={part}>{part}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Requested By *</Label>
                  <Select value={formData.requested_by} onValueChange={(v) => setFormData({...formData, requested_by: v})}>
                    <SelectTrigger data-testid="select-doctor">
                      <SelectValue placeholder="Select doctor" />
                    </SelectTrigger>
                    <SelectContent>
                      {doctors.map(d => (
                        <SelectItem key={d.id} value={d.id}>Dr. {d.full_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Urgency</Label>
                    <Select value={formData.urgency} onValueChange={(v) => setFormData({...formData, urgency: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                        <SelectItem value="stat">STAT</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Price (₹)</Label>
                    <Input
                      type="number"
                      value={formData.price}
                      onChange={(e) => setFormData({...formData, price: parseFloat(e.target.value) || 0})}
                      data-testid="input-price"
                    />
                  </div>
                </div>
                <div>
                  <Label>Clinical Notes</Label>
                  <Textarea
                    value={formData.clinical_notes}
                    onChange={(e) => setFormData({...formData, clinical_notes: e.target.value})}
                    placeholder="Clinical indication / reason for study"
                    data-testid="input-notes"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" className="bg-red-500 hover:bg-red-600" data-testid="submit-imaging-test">Order Test</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <div className="page-content">
        <Card>
          <CardHeader className="border-b border-slate-100">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold font-[Manrope] flex items-center gap-2">
                <Scan className="w-5 h-5 text-red-500" />
                Imaging Orders
              </CardTitle>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-500">Loading imaging tests...</div>
            ) : tests.length === 0 ? (
              <div className="p-8 text-center text-slate-500">No imaging tests found.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Test ID</TableHead>
                    <TableHead>Patient</TableHead>
                    <TableHead>Study</TableHead>
                    <TableHead>Urgency</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Ordered</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tests.map((test) => (
                    <TableRow key={test.id} data-testid={`imaging-test-row-${test.id}`}>
                      <TableCell className="font-mono text-sm">{test.test_id}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{test.patient?.full_name}</p>
                          <p className="text-xs text-slate-500">{test.patient?.patient_id}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{test.test_type}</p>
                          <p className="text-xs text-slate-500">{test.body_part}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={test.urgency === 'stat' ? 'bg-red-100 text-red-800' : test.urgency === 'urgent' ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-800'}>
                          {test.urgency?.toUpperCase()}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatCurrency(test.price)}</TableCell>
                      <TableCell>{formatDateTime(test.created_at)}</TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(test.status)}>{test.status}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {hasRole(['super_admin', 'admin', 'lab_technician']) && test.status !== 'completed' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openResultDialog(test)}
                            data-testid={`update-${test.id}`}
                          >
                            <ClipboardCheck className="w-4 h-4 mr-1" />
                            Report
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Report Dialog */}
      <Dialog open={resultDialogOpen} onOpenChange={setResultDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">Update Imaging Report</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Study</Label>
              <p className="font-medium">{selectedTest?.test_type} - {selectedTest?.body_part}</p>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={resultData.status} onValueChange={(v) => setResultData({...resultData, status: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Report / Findings</Label>
              <Textarea
                value={resultData.report}
                onChange={(e) => setResultData({...resultData, report: e.target.value})}
                placeholder="Enter radiology findings and impressions"
                rows={6}
                data-testid="input-report"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setResultDialogOpen(false)}>Cancel</Button>
              <Button className="bg-red-500 hover:bg-red-600" onClick={handleUpdateResult} data-testid="save-report">Save Report</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Suggest New Imaging Test Modal */}
      <SuggestItemModal
        open={showSuggestModal}
        onClose={() => setShowSuggestModal(false)}
        itemType="imaging_test"
        onSuggestionCreated={() => {
          toast.info('Your imaging test suggestion has been submitted for approval');
        }}
      />
        </>
      )}
    </div>
  );
};

export default ImagingTestList;
